import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BirdIcon,
  CatIcon,
  CheckIcon,
  DogIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PawPrintIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from "@phosphor-icons/react";
import { api } from "./lib/api.js";
import { buscarDireccion, obtenerPosicionActual } from "./lib/geocodificacion.js";
import MenuReportar from "./MenuReportar.jsx";
import isotipo from "../../TALLER/isotipo.webp";

/// Extremos de los deslizadores. Los valores iniciales DEBEN coincidir con
/// estos: un rango por defecto mas estrecho es un filtro invisible que oculta
/// reportes sin que el usuario sepa por que. Pasaba con edadMin/edadMax en
/// 10-40, que dejaba fuera a cualquier persona de mas de 40 anos.
export const RANGO_EDAD = { min: 0, max: 110 };
export const RANGO_ESTATURA = { min: 0.3, max: 2.6 };

const FILTROS_INICIALES = {
  personas: {
    tipo: "", sexo: "", ciudad: "", contextura: "", colorCabello: [],
    edadMin: RANGO_EDAD.min, edadMax: RANGO_EDAD.max,
    estaturaMin: RANGO_ESTATURA.min, estaturaMax: RANGO_ESTATURA.max,
  },
  mascotas: { tipo: "", tipoMascota: "", sexo: "", ciudad: "", tamano: [], colorPrincipalPelaje: [] },
};

const opcionesTipo = [
  { valor: "", etiqueta: "Todos" },
  { valor: "DESAPARECIDO", etiqueta: "Buscando" },
  { valor: "ENCONTRADO", etiqueta: "Encontrado" },
];

const opcionesMascota = [
  { valor: "PERRO", texto: "Perros", Icon: DogIcon },
  { valor: "GATO", texto: "Gatos", Icon: CatIcon },
  { valor: "OTRO", texto: "Otros", Icon: BirdIcon },
];

const opcionesSexo = {
  mascotas: [["", "Todos"], ["MACHO", "Macho"], ["HEMBRA", "Hembra"]],
  personas: [["", "Todos"], ["HOMBRE", "Hombre"], ["MUJER", "Mujer"], ["NO_ESPECIFICA", "No especifica"]],
};

const tamanosBase = [
  ["MUY_PEQUENO", "Muy pequeño"],
  ["PEQUENO", "Pequeño"],
  ["MEDIANO", "Mediano"],
  ["GRANDE", "Grande"],
  ["MUY_GRANDE", "Muy grande"],
];

const opcionesColor = [
  ["NEGRO", "Negro", "#272727"], ["BLANCO", "Blanco", "#f7f7f2"],
  ["MARRON", "Marrón", "#70452f"], ["CAFE_CLARO", "Café claro", "#a87855"],
  ["BEIGE", "Beige", "#d8bd91"], ["CREMA", "Crema", "#f0dfbd"],
  ["DORADO", "Dorado", "#d79a38"], ["AMARILLO", "Amarillo", "#eac94f"],
  ["ROJIZO", "Rojizo", "#a8492b"], ["GRIS", "Gris", "#85888b"],
  ["CANELA", "Canela", "#b86e36"], ["ATIGRADO", "Atigrado", "#765a44"],
  ["MANCHADO", "Manchado", "linear-gradient(135deg, #f4f0e5 45%, #434343 46% 60%, #a76d46 61%)"],
];

const opcionesContextura = [["", "Todas"], ["DELGADA", "Delgada"], ["MEDIANA", "Mediana"], ["ROBUSTA", "Robusta"]];

const opcionesCabello = [
  ["NEGRO", "Negro", "#272727"], ["CASTANO_OSCURO", "Castaño oscuro", "#493124"],
  ["CASTANO", "Castaño", "#704a32"], ["CASTANO_CLARO", "Castaño claro", "#a6754d"],
  ["RUBIO", "Rubio", "#d6b56e"], ["RUBIO_OSCURO", "Rubio oscuro", "#a3834b"],
  ["PELIRROJO", "Pelirrojo", "#a74829"], ["CANOSO", "Canoso", "#aeb2b5"],
  ["TENIDO", "Teñido", "linear-gradient(135deg, #7746ae, #dc5e9f, #2b9dba)"],
  ["CALVO", "Calvo", "#e4bd99"],
];

function etiqueta(valor) {
  if (!valor) return "";
  return valor.toLowerCase().replaceAll("_", " ").replace(/^./, (letra) => letra.toUpperCase());
}

function tiempoRelativo(fecha) {
  const minutos = Math.max(1, Math.floor((Date.now() - new Date(fecha).getTime()) / 60000));
  if (minutos < 60) return `Hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;
  return `Hace ${Math.floor(horas / 24)} d`;
}

function estadoVisible(tipo) {
  return tipo === "ENCONTRADO" ? "Encontrado" : "Buscando";
}

function TarjetaResultado({ reporte, entidad, onOpenMascota, onOpenPersona }) {
  const esMascota = entidad === "mascotas";
  let detalle;
  if (esMascota) {
    detalle = [etiqueta(reporte.tipoMascota), etiqueta(reporte.sexo)].filter(Boolean).join(" · ");
  } else if (reporte.edadAproximada != null) {
    detalle = `${reporte.edadAproximada} años aprox.`;
  } else {
    detalle = etiqueta(reporte.sexo);
  }

  let foto = esMascota ? <PawPrintIcon weight="fill" /> : <UserIcon weight="fill" />;
  if (reporte.urlThumb) foto = <img src={reporte.urlThumb} alt="" loading="lazy" />;
  const abrirDetalle = () => esMascota ? onOpenMascota(reporte.id) : onOpenPersona(reporte.id);

  return (
    <article className={`search-result search-result--${esMascota ? "pet" : "person"} is-clickable`} role="link" tabIndex="0" onClick={abrirDetalle} onKeyDown={(evento) => { if (evento.key === "Enter" || evento.key === " ") { evento.preventDefault(); abrirDetalle(); } }}>
      <div className="search-result__photo">
        {foto}
      </div>
      <div className="search-result__content">
        <h2>{reporte.nombre || (esMascota ? "Mascota sin nombre" : "Persona sin identificar")}</h2>
        <p className="search-result__detail">{detalle || "Información por confirmar"}</p>
        <p className="search-result__location"><MapPinIcon weight="fill" />{reporte.ubicacion || "Ubicación por confirmar"}</p>
        {!!reporte.chips?.length && <div className="search-result__chips">{reporte.chips.map((chip) => <span key={chip}>{etiqueta(chip)}</span>)}</div>}
      </div>
      <div className="search-result__aside">
        <span>{estadoVisible(reporte.tipo)}</span>
        <time dateTime={reporte.createdAt}>{tiempoRelativo(reporte.createdAt)}</time>
        <ArrowRightIcon weight="bold" aria-hidden="true" />
      </div>
    </article>
  );
}

function SelectorMascota({ valor, onChange }) {
  return <div className="pet-filter-options">{opcionesMascota.map(({ valor: opcion, texto, Icon }) => (
    <button className={valor === opcion ? "is-selected" : ""} type="button" onClick={() => onChange(valor === opcion ? "" : opcion)} key={opcion}>
      <Icon weight={valor === opcion ? "fill" : "duotone"} />
      <span>{texto}</span>
      {valor === opcion && <CheckIcon weight="bold" />}
    </button>
  ))}</div>;
}

function ModalFiltros({ entidad, filtros, onClose, onApply }) {
  const [borrador, setBorrador] = useState(filtros);
  const esMascota = entidad === "mascotas";
  const cambiar = (campo, valor) => setBorrador((actual) => ({ ...actual, [campo]: valor }));
  const limpiar = () => setBorrador({ ...FILTROS_INICIALES[entidad] });

  function cambiarTipoMascota(valor) {
    setBorrador((actual) => ({
      ...actual,
      tipoMascota: valor,
      ...(!valor && { tamano: [], colorPrincipalPelaje: [] }),
    }));
  }

  function alternarLista(campo, valor) {
    setBorrador((actual) => {
      const lista = actual[campo] ?? [];
      return { ...actual, [campo]: lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor] };
    });
  }

  useEffect(() => {
    const cerrar = (evento) => evento.key === "Escape" && onClose();
    document.addEventListener("keydown", cerrar);
    document.body.classList.add("modal-open");
    return () => { document.removeEventListener("keydown", cerrar); document.body.classList.remove("modal-open"); };
  }, [onClose]);

  return (
    <div className="filter-modal" role="dialog" aria-modal="true" aria-labelledby="filter-title" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="filter-panel">
        <header><div><span>Refina los resultados</span><h2 id="filter-title">Filtros de {esMascota ? "mascotas" : "personas"}</h2></div><button type="button" onClick={onClose} aria-label="Cerrar filtros"><XIcon weight="bold" /></button></header>

        {esMascota && <fieldset><legend>Tipo de mascota</legend><SelectorMascota valor={borrador.tipoMascota} onChange={cambiarTipoMascota} /></fieldset>}

        {esMascota && borrador.tipoMascota && <>
          <fieldset><legend>Tamaños <span className="filter-legend-note">Puedes elegir varios</span></legend><div className="multi-filter-grid">{tamanosBase.map(([valor, texto]) => <button type="button" className={borrador.tamano.includes(valor) ? "is-selected" : ""} onClick={() => alternarLista("tamano", valor)} key={valor}>{texto}{borrador.tamano.includes(valor) && <CheckIcon weight="bold" />}</button>)}</div></fieldset>
          <fieldset><legend>Color predominante <span className="filter-legend-note">Puedes elegir varios</span></legend><div className="color-filter-grid">{opcionesColor.map(([valor, texto, muestra]) => <button type="button" className={borrador.colorPrincipalPelaje.includes(valor) ? "is-selected" : ""} onClick={() => alternarLista("colorPrincipalPelaje", valor)} key={valor}><i style={{ background: muestra }} />{texto}{borrador.colorPrincipalPelaje.includes(valor) && <CheckIcon weight="bold" />}</button>)}</div></fieldset>
        </>}

        <fieldset><legend>Tipo de reporte</legend><div className="filter-pills">{opcionesTipo.map((opcion) => <button type="button" className={borrador.tipo === opcion.valor ? "is-selected" : ""} onClick={() => cambiar("tipo", opcion.valor)} key={opcion.etiqueta}>{opcion.etiqueta}</button>)}</div></fieldset>

        <label className="filter-field">Ciudad<input value={borrador.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} maxLength="80" placeholder="Ej: Medellín" /></label>

        <fieldset><legend>Sexo</legend><div className="filter-pills">
          {opcionesSexo[entidad].map(([valor, texto]) => <button type="button" className={borrador.sexo === valor ? "is-selected" : ""} onClick={() => cambiar("sexo", valor)} key={texto}>{texto}</button>)}
        </div></fieldset>

        {!esMascota && <fieldset className="age-range"><legend>Rango de edad</legend><div><strong>{borrador.edadMin} años</strong><span>a</span><strong>{borrador.edadMax} años</strong></div><label>Edad mínima<input type="range" min="0" max="110" value={borrador.edadMin} onChange={(e) => cambiar("edadMin", Math.min(Number(e.target.value), borrador.edadMax))} /></label><label>Edad máxima<input type="range" min="0" max="110" value={borrador.edadMax} onChange={(e) => cambiar("edadMax", Math.max(Number(e.target.value), borrador.edadMin))} /></label></fieldset>}

        {!esMascota && <>
          <fieldset><legend>Contextura</legend><div className="filter-pills">{opcionesContextura.map(([valor, texto]) => <button type="button" className={borrador.contextura === valor ? "is-selected" : ""} onClick={() => cambiar("contextura", valor)} key={texto}>{texto}</button>)}</div></fieldset>
          <fieldset><legend>Color de cabello <span className="filter-legend-note">Puedes elegir varios</span></legend><div className="color-filter-grid">{opcionesCabello.map(([valor, texto, muestra]) => <button type="button" className={borrador.colorCabello.includes(valor) ? "is-selected" : ""} onClick={() => alternarLista("colorCabello", valor)} key={valor}><i style={{ background: muestra }} />{texto}{borrador.colorCabello.includes(valor) && <CheckIcon weight="bold" />}</button>)}</div></fieldset>
          <fieldset className="age-range height-range"><legend>Rango de estatura</legend><div><strong>{borrador.estaturaMin.toFixed(2)} m</strong><span>a</span><strong>{borrador.estaturaMax.toFixed(2)} m</strong></div><label>Estatura mínima<input type="range" min="0.3" max="2.6" step="0.01" value={borrador.estaturaMin} onChange={(e) => cambiar("estaturaMin", Math.min(Number(e.target.value), borrador.estaturaMax))} /></label><label>Estatura máxima<input type="range" min="0.3" max="2.6" step="0.01" value={borrador.estaturaMax} onChange={(e) => cambiar("estaturaMax", Math.max(Number(e.target.value), borrador.estaturaMin))} /></label></fieldset>
        </>}

        <footer><button type="button" className="filter-clear" onClick={limpiar}>Limpiar</button><button type="button" className="filter-apply" onClick={() => onApply(borrador)}>Ver resultados</button></footer>
      </section>
    </div>
  );
}

export default function BusquedaReportes({ entidadInicial = "mascotas", onBack, onNavigate, onOpenMascota, onOpenPersona }) {
  const [entidad, setEntidad] = useState(entidadInicial);
  const [texto, setTexto] = useState("");
  const [textoEstable, setTextoEstable] = useState("");
  const [filtrosPorEntidad, setFiltrosPorEntidad] = useState(FILTROS_INICIALES);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState("");
  const [estadoUbicacion, setEstadoUbicacion] = useState("buscando");
  /// Ciudad detectada por GPS. Preferencia de orden, nunca filtro.
  const [ciudadCercana, setCiudadCercana] = useState("");
  const centinelaRef = useRef(null);
  const filtros = filtrosPorEntidad[entidad];

  useEffect(() => {
    let vigente = true;
    obtenerPosicionActual()
      .then(({ latitud, longitud }) => buscarDireccion(latitud, longitud))
      .then(({ ciudad }) => {
        if (!vigente) return;
        // La ubicacion detectada ORDENA, no filtra: se guarda aparte de los
        // filtros. Rellenar el filtro `ciudad` dejaba invisibles los reportes
        // de otras ciudades — alguien en Medellin buscando a un familiar
        // perdido en Pereira veia una lista vacia.
        if (ciudad) setCiudadCercana(ciudad);
        setEstadoUbicacion("listo");
      })
      .catch(() => { if (vigente) setEstadoUbicacion("no-disponible"); });
    return () => { vigente = false; };
  }, []);

  useEffect(() => {
    const temporizador = window.setTimeout(() => setTextoEstable(texto.trim()), 350);
    return () => window.clearTimeout(temporizador);
  }, [texto]);

  const consultaBase = useMemo(() => {
    const consulta = {
      ...filtros,
      ...(textoEstable.length >= 2 ? { q: textoEstable } : {}),
      // Solo tiene sentido si el usuario no eligio una ciudad a mano: en ese
      // caso su filtro manda y ordenar por cercania sobra.
      ...(ciudadCercana && !filtros.ciudad ? { ciudadCercana } : {}),
      limite: 20,
    };
    // Un rango completo no es un filtro: no se manda. Ademas de ahorrar
    // parametros, evita excluir reportes sin edad o estatura registradas — en
    // SQL, `edad BETWEEN 0 AND 110` deja fuera los NULL.
    if (entidad === "personas") {
      if (filtros.estaturaMin === RANGO_ESTATURA.min && filtros.estaturaMax === RANGO_ESTATURA.max) {
        delete consulta.estaturaMin;
        delete consulta.estaturaMax;
      }
      if (filtros.edadMin === RANGO_EDAD.min && filtros.edadMax === RANGO_EDAD.max) {
        delete consulta.edadMin;
        delete consulta.edadMax;
      }
    }
    return consulta;
  }, [entidad, filtros, textoEstable, ciudadCercana]);

  useEffect(() => {
    if (estadoUbicacion === "buscando") return undefined;
    const controlador = new AbortController();
    setEstado("cargando");
    setError("");
    setResultados([]);
    setCursor(null);
    api.buscar[entidad](consultaBase, { signal: controlador.signal })
      .then((pagina) => { setResultados(pagina.resultados ?? []); setCursor(pagina.siguienteCursor ?? null); setEstado("listo"); })
      .catch((err) => { if (err.name !== "AbortError") { setError(err.message); setEstado("error"); } });
    return () => controlador.abort();
  }, [entidad, consultaBase, estadoUbicacion]);

  useEffect(() => {
    const nodo = centinelaRef.current;
    if (!nodo || !cursor || estado !== "listo") return undefined;
    const observador = new IntersectionObserver(([entrada]) => {
      if (!entrada.isIntersecting) return;
      observador.disconnect();
      setEstado("cargando-mas");
      api.buscar[entidad]({ ...consultaBase, cursor })
        .then((pagina) => {
          setResultados((actuales) => {
            const ids = new Set(actuales.map((item) => item.id));
            return [...actuales, ...(pagina.resultados ?? []).filter((item) => !ids.has(item.id))];
          });
          setCursor(pagina.siguienteCursor ?? null);
          setEstado("listo");
        })
        .catch((err) => { setError(err.message); setEstado("error-mas"); });
    }, { rootMargin: "300px" });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [consultaBase, cursor, entidad, estado]);

  function cambiarEntidad(nuevaEntidad) {
    setEntidad(nuevaEntidad);
    setTexto("");
    setTextoEstable("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function aplicarFiltros(nuevos) {
    setFiltrosPorEntidad((actual) => ({ ...actual, [entidad]: nuevos }));
    setModalAbierto(false);
  }

  const camposRango = new Set(["edadMin", "edadMax", "estaturaMin", "estaturaMax"]);
  const cantidadFiltros = Object.entries(filtros).filter(([clave, valor]) => !camposRango.has(clave) && (Array.isArray(valor) ? valor.length > 0 : Boolean(valor))).length
    + (entidad === "personas" && (filtros.edadMin !== RANGO_EDAD.min || filtros.edadMax !== RANGO_EDAD.max) ? 1 : 0)
    + (entidad === "personas" && (filtros.estaturaMin !== RANGO_ESTATURA.min || filtros.estaturaMax !== RANGO_ESTATURA.max) ? 1 : 0);

  return (
    <div className="search-page">
      <header className="search-header"><button type="button" onClick={onBack} aria-label="Volver al inicio"><ArrowLeftIcon weight="bold" /></button><div className="search-brand"><img src={isotipo} alt="" /><span><strong>encuentra<span>y ayuda</span></strong><small>Personas y mascotas, juntos de nuevo.</small></span></div><MenuReportar onNavigate={onNavigate} /></header>

      <div className="search-controls">
        <div className="entity-tabs" role="tablist" aria-label="Tipo de búsqueda"><button role="tab" aria-selected={entidad === "personas"} className={entidad === "personas" ? "is-active" : ""} onClick={() => cambiarEntidad("personas")}><UsersIcon weight="fill" />Personas</button><button role="tab" aria-selected={entidad === "mascotas"} className={entidad === "mascotas" ? "is-active" : ""} onClick={() => cambiarEntidad("mascotas")}><PawPrintIcon weight="fill" />Mascotas</button></div>
        <div className="search-tools"><label><MagnifyingGlassIcon weight="bold" /><span className="sr-only">Buscar {entidad}</span><input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={`Buscar ${entidad}…`} maxLength="60" /></label><button className="filter-button" type="button" onClick={() => setModalAbierto(true)}><FunnelIcon weight="bold" />Filtros{cantidadFiltros > 0 && <span>{cantidadFiltros}</span>}</button></div>
      </div>

      <main className="search-results" aria-live="polite">
        {estadoUbicacion === "buscando" && <div className="search-loading"><span className="loader" />Detectando tu ciudad…</div>}
        {estadoUbicacion !== "buscando" && estado === "cargando" && <div className="search-loading"><span className="loader" />Buscando reportes…</div>}
        {estado === "error" && <div className="search-empty"><strong>No pudimos cargar los reportes.</strong><p>{error}</p></div>}
        {/* `entidad` y no `esMascota`: esa constante vive en TarjetaResultado y
            en ModalFiltros, no en este ambito. Usarla aqui lanzaba
            ReferenceError y tumbaba la pantalla entera — y solo se notaba con
            una busqueda sin resultados, que es cuando se renderiza este bloque. */}
        {estado === "listo" && resultados.length === 0 && <div className="search-empty">{entidad === "mascotas" ? <PawPrintIcon weight="duotone" /> : <UsersIcon weight="duotone" />}<strong>No encontramos coincidencias.</strong><p>Prueba con otra búsqueda o limpia los filtros.</p></div>}
        {resultados.map((reporte) => <TarjetaResultado reporte={reporte} entidad={entidad} onOpenMascota={onOpenMascota} onOpenPersona={onOpenPersona} key={reporte.id} />)}
        <div className="infinite-sentinel" ref={centinelaRef}>{estado === "cargando-mas" && <><span className="loader" />Cargando más resultados…</>}{estado === "error-mas" && <span>{error}</span>}{estado === "listo" && resultados.length > 0 && !cursor && <span>Ya viste todos los resultados.</span>}</div>
      </main>

      {modalAbierto && <ModalFiltros entidad={entidad} filtros={filtros} onClose={() => setModalAbierto(false)} onApply={aplicarFiltros} />}
    </div>
  );
}
